import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FileText,
  Settings,
  Download,
  LogOut,
  Shield,
  Users,
  ScrollText,
  BarChart3,
  UsersRound,
  AlertTriangle,
  Calculator,
  Activity,
  MessageSquare,
  Layers,
  HardDrive,
  ChevronRight,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/hooks/useAuth';
import { useAdmin } from '@/hooks/useAdmin';
import { getPendingDraftStatic } from '@/hooks/useDraftProtection';
import { RateLimitIndicator } from '@/components/RateLimitIndicator';
import { cn } from '@/lib/utils';
import { useEffect, useState } from 'react';
import AtlasDraftIcon from '@/assets/atlasdraft-icon.svg';

interface MobileMenuSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface MenuItemProps {
  icon: React.ElementType;
  label: string;
  onClick: () => void;
  variant?: 'default' | 'destructive';
}

function MenuItem({ icon: Icon, label, onClick, variant = 'default' }: MenuItemProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors',
        variant === 'destructive' 
          ? 'text-destructive hover:bg-destructive/10' 
          : 'text-foreground hover:bg-muted'
      )}
    >
      <Icon className="w-5 h-5" />
      <span className="flex-1 text-left font-medium">{label}</span>
      <ChevronRight className="w-4 h-4 text-muted-foreground" />
    </button>
  );
}

export function MobileMenuSheet({ open, onOpenChange }: MobileMenuSheetProps) {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { isAdmin } = useAdmin();
  const [hasPendingDraft, setHasPendingDraft] = useState(false);

  useEffect(() => {
    if (open) {
      const draft = getPendingDraftStatic();
      setHasPendingDraft(draft !== null);
    }
  }, [open]);

  const handleNavigate = (path: string) => {
    navigate(path);
    onOpenChange(false);
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/auth');
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[85vh] rounded-t-2xl px-0">
        <SheetHeader className="px-4 pb-2">
          <div className="flex items-center gap-3">
            <img src={AtlasDraftIcon} alt="AtlasDraft" className="w-8 h-8" />
            <SheetTitle className="font-logo text-xl">AtlasDraft</SheetTitle>
          </div>
        </SheetHeader>

        <ScrollArea className="h-full pb-8">
          <div className="px-2 py-2 space-y-1">
            {/* Pending Draft Indicator */}
            {hasPendingDraft && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mx-2 mb-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400"
              >
                <HardDrive className="w-4 h-4 animate-pulse" />
                <span className="text-sm font-medium">Draft saved locally</span>
              </motion.div>
            )}

            {/* Rate Limit Indicator */}
            <div className="px-2 mb-2">
              <RateLimitIndicator collapsed={false} />
            </div>

            <Separator className="my-2" />

            {/* Main Menu Items */}
            <MenuItem
              icon={FileText}
              label="Templates"
              onClick={() => handleNavigate('/templates')}
            />
            <MenuItem
              icon={Download}
              label="Install App"
              onClick={() => handleNavigate('/install')}
            />
            <MenuItem
              icon={Settings}
              label="Settings"
              onClick={() => handleNavigate('/settings')}
            />

            {/* Admin Section */}
            {isAdmin && (
              <>
                <Separator className="my-3" />
                <p className="px-4 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Admin
                </p>
                <MenuItem
                  icon={Shield}
                  label="Admin Settings"
                  onClick={() => handleNavigate('/admin/settings')}
                />
                <MenuItem
                  icon={Layers}
                  label="Template Manager"
                  onClick={() => handleNavigate('/admin/templates')}
                />
                <MenuItem
                  icon={MessageSquare}
                  label="AI Prompts"
                  onClick={() => handleNavigate('/admin/ai-prompts')}
                />
                <MenuItem
                  icon={Users}
                  label="User Management"
                  onClick={() => handleNavigate('/admin/users')}
                />
                <MenuItem
                  icon={ScrollText}
                  label="Audit Logs"
                  onClick={() => handleNavigate('/admin/audit-logs')}
                />
                <MenuItem
                  icon={BarChart3}
                  label="AI Analytics"
                  onClick={() => handleNavigate('/admin/ai-analytics')}
                />
                <MenuItem
                  icon={UsersRound}
                  label="User Analytics"
                  onClick={() => handleNavigate('/admin/user-analytics')}
                />
                <MenuItem
                  icon={AlertTriangle}
                  label="Usage Alerts"
                  onClick={() => handleNavigate('/admin/alerts')}
                />
                <MenuItem
                  icon={Calculator}
                  label="Pricing & Margins"
                  onClick={() => handleNavigate('/admin/pricing')}
                />
                <MenuItem
                  icon={Activity}
                  label="Platform Health"
                  onClick={() => handleNavigate('/admin/platform-health')}
                />
              </>
            )}

            <Separator className="my-3" />

            {/* Logout */}
            <MenuItem
              icon={LogOut}
              label="Log Out"
              onClick={handleLogout}
              variant="destructive"
            />
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
